import {
  ArnDelegateNftService,
  ArnInternalNftService,
} from '../ArnDelegateNftService';
import {
  ABI_IERC721Metadata,
  ArnNftMetadata,
  Chains,
  getJsonRpcProvider,
  NftMetadataStandard,
} from '@arianee/arn-types';
import { Contract } from 'ethers';
import {
  ArnErc721NftService,
  Erc721SearchCriteria,
} from './ArnErc721NftService';
import { ArnClientError } from '../../ArnClientError';

export class DefaultErc721NftService
  extends ArnDelegateNftService
  implements ArnErc721NftService
{
  constructor(nft: ArnInternalNftService) {
    super(nft);
  }

  async getToken(searchCriteria: Erc721SearchCriteria) {
    const log = this.log;
    log.debug('getToken', searchCriteria);
    const { contractAddress, tokenId } = searchCriteria;
    const chainId = searchCriteria.chainId || Chains.Ethereum;
    const jsonRpcProvider = getJsonRpcProvider(chainId);
    let tokenURI: string | undefined;
    try {
      const erc721MetadataContract = new Contract(
        contractAddress,
        ABI_IERC721Metadata,
        jsonRpcProvider
      );
      tokenURI = await erc721MetadataContract.tokenURI(tokenId);
      if (!tokenURI) {
        throw new ArnClientError('Empty tokenURI response');
      }
    } catch (err) {
      throw new ArnClientError(
        `Could not get tokenURI from contract ${tokenId}: ${err}`
      );
    }
    let metadata: NftMetadataStandard | undefined;
    try {
      metadata = await this.handleIpfsFor(tokenURI, (httpsUrl: string) =>
        this.http.fetchJson<NftMetadataStandard>(httpsUrl)
      );
      if (!metadata) {
        throw new ArnClientError('Empty metadata response');
      }
    } catch (err) {
      throw new Error(
        `Could not fetch metadata from tokenURI ${tokenURI}: ${err}`
      );
    }
    const imageUrl = metadata.image;
    // Fetching image mime type
    let imageMimeType: string | undefined = undefined;
    let imageHttps;
    if (imageUrl) {
      try {
        const imageResponse = await this.handleIpfsFor(
          imageUrl,
          async (httpsUrl: string) => {
            const fetchResult = await this.http.fetch(httpsUrl);
            if (fetchResult) {
              imageHttps = httpsUrl;
            }
            return fetchResult;
          }
        );
        if (imageResponse) {
          const contentType = imageResponse.headers.get('content-type');
          if (contentType) {
            imageMimeType = contentType.split(';')[0];
          }
        }
      } catch (err) {
        log.warn(`Could not resolve image mime of ${imageUrl}: ${err}`);
      }
    }
    const token: ArnNftMetadata = {
      tokenId,
      imageMimeType,
      ...metadata,
      imageHttps,
    };
    return token;
  }

  protected async handleIpfsFor<T>(
    url: string,
    fetcher: (httpsUrl: string, init?: RequestInit) => Promise<T>,
    init?: RequestInit
  ) {
    const log = this.log;
    log.debug('handleIpfsFor', url, init);
    let result: T | undefined;
    let providerIndex = 0;
    const isHttpOrHttps =
      url.startsWith('http://') || url.startsWith('https://');
    const isIpfs = url.startsWith('ipfs://');
    if (!isHttpOrHttps && !isIpfs) {
      throw new ArnClientError(`Unsupported URL protocol: "${url}"`);
    }
    const ipfsUrls = this.config.erc721.ipfs.baseUrls;
    do {
      let httpsUrl: string;
      if (isIpfs) {
        const baseUrl = ipfsUrls[providerIndex];
        httpsUrl = url.replace('ipfs://', baseUrl);
        this.log.debug(`IPFS URI converted to: ${httpsUrl}`);
        providerIndex++;
      } else {
        httpsUrl = url;
      }
      try {
        result = await fetcher(httpsUrl, init);
      } catch (e) {
        log.warn('Could not fetch', httpsUrl, 'because of', e);
      }
    } while (
      isIpfs &&
      providerIndex < this.config.erc721.ipfs.baseUrls.length &&
      !result
    );
    log.debug('handleIpfsFor result', result);
    return result;
  }
}
